/**
 * Shared pool times that exist once per squad.
 *
 * WASC stores Monday morning (and the other shared times) as two sessions:
 * one Performance, one Development. Performance swimmers may also book
 * Development, so the same pool time shows twice and can be booked twice.
 *
 * The session they should book is the Development one. Performance sessions
 * that do not overlap a Development session stay bookable — that is the
 * Tuesday and Thursday morning Performance block.
 */

import { dayOfWeekIso, timeToMinutes } from './time';

export const SHARED_SLOT_RETIRED_REASON =
  'Shared with the Development session at this time. Book that session. This row is kept so it can be restored.';

export type SharedSlotRef = {
  id: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  status: string;
  squadName: string | null | undefined;
  poolKey: string | null | undefined;
};

/** Weekly template shape. `dayOfWeek` is 0=Sunday … 6=Saturday. */
export type SharedSlotTemplate = {
  id: string;
  squadName: string | null | undefined;
  poolKey: string | null | undefined;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
};

function normSquad(name: string | null | undefined): string {
  return (name ?? '').trim().toLowerCase();
}

/** Directed: swimmers from `fromSquad` attend `intoSquad` sessions. */
export function squadGuestsInto(
  fromSquad: string | null | undefined,
  intoSquad: string | null | undefined,
): boolean {
  return normSquad(fromSquad) === 'performance' && normSquad(intoSquad) === 'development';
}

/**
 * Squad line on a session card. Development sessions that Performance
 * swimmers can also book name both squads. Other squads keep their own name.
 */
export function sharedSessionSquadLabel(squadName: string | null | undefined): string {
  const name = (squadName ?? '').trim();
  if (!name) return 'Session';
  if (squadGuestsInto('Performance', name)) return `${name} · Performance`;
  return name;
}

export function timesOverlap(startA: string, endA: string, startB: string, endB: string): boolean {
  const a0 = timeToMinutes(startA);
  const a1 = timeToMinutes(endA);
  const b0 = timeToMinutes(startB);
  const b1 = timeToMinutes(endB);
  if (![a0, a1, b0, b1].every((n) => Number.isFinite(n))) return false;
  return a0 < b1 && b0 < a1;
}

export function samePool(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a === b;
}

/**
 * Tuesday and Thursday mornings are the Performance-only block.
 * They stay on the booking list even when another squad shares a clock time.
 */
export function isKeptPerformanceMorning(input: {
  squadName: string | null | undefined;
  dayOfWeek: number;
  startTime: string;
}): boolean {
  if (normSquad(input.squadName) !== 'performance') return false;
  if (input.dayOfWeek !== 2 && input.dayOfWeek !== 4) return false;
  const start = timeToMinutes(input.startTime);
  return Number.isFinite(start) && start < 12 * 60;
}

export function isKeptPerformanceMorningOnDate(input: {
  squadName: string | null | undefined;
  sessionDate: string;
  startTime: string;
}): boolean {
  return isKeptPerformanceMorning({
    squadName: input.squadName,
    dayOfWeek: dayOfWeekIso(input.sessionDate),
    startTime: input.startTime,
  });
}

function slotsOverlap(a: SharedSlotRef, b: SharedSlotRef): boolean {
  return (
    a.sessionDate === b.sessionDate &&
    samePool(a.poolKey, b.poolKey) &&
    timesOverlap(a.startTime, a.endTime, b.startTime, b.endTime)
  );
}

/**
 * Performance session id → Development session id that covers the same pool time.
 * Tuesday and Thursday morning Performance sessions are never hidden.
 * Only scheduled sessions take part.
 */
export function sharedSlotHides(sessions: readonly SharedSlotRef[]): Map<string, string> {
  const open = sessions.filter((s) => s.status === 'scheduled');
  const hidden = new Map<string, string>();
  for (const candidate of open) {
    if (
      isKeptPerformanceMorningOnDate({
        squadName: candidate.squadName,
        sessionDate: candidate.sessionDate,
        startTime: candidate.startTime,
      })
    ) {
      continue;
    }
    let keeper: SharedSlotRef | null = null;
    for (const other of open) {
      if (other.id === candidate.id) continue;
      if (!slotsOverlap(candidate, other)) continue;
      if (!squadGuestsInto(candidate.squadName, other.squadName)) continue;
      if (!keeper || other.id < keeper.id) keeper = other;
    }
    if (keeper) hidden.set(candidate.id, keeper.id);
  }
  return hidden;
}

export function collapseSharedSlots<T>(
  sessions: readonly T[],
  toRef: (session: T) => SharedSlotRef,
  mergeHidden?: (keeper: T, hidden: readonly T[]) => T,
): T[] {
  const refs = sessions.map(toRef);
  const hide = sharedSlotHides(refs);
  const byId = new Map(sessions.map((session, index) => [refs[index]!.id, session]));
  const hiddenByKeeper = new Map<string, T[]>();
  for (const [hiddenId, keeperId] of hide) {
    const hidden = byId.get(hiddenId);
    if (!hidden) continue;
    const list = hiddenByKeeper.get(keeperId) ?? [];
    list.push(hidden);
    hiddenByKeeper.set(keeperId, list);
  }
  const out: T[] = [];
  for (let i = 0; i < sessions.length; i++) {
    const id = refs[i]!.id;
    if (hide.has(id)) continue;
    const session = sessions[i]!;
    const hidden = hiddenByKeeper.get(id) ?? [];
    out.push(hidden.length > 0 && mergeHidden ? mergeHidden(session, hidden) : session);
  }
  return out;
}

export type OverlapBookingDecision =
  | { action: 'allow' }
  | { action: 'reject'; message: string }
  | { action: 'absorb'; duplicateIds: string[] };

/**
 * Booking `target` while `others` are the other scheduled sessions that day.
 * `occupyingIds` are sessions where this swimmer already has a booking
 * (booked, arrived, or waitlist).
 */
export function decideOverlapBooking(input: {
  target: SharedSlotRef;
  others: readonly SharedSlotRef[];
  occupyingIds: readonly string[];
}): OverlapBookingDecision {
  const occupying = new Set(input.occupyingIds);
  const overlapping = input.others.filter(
    (other) => other.id !== input.target.id && other.status === 'scheduled' && slotsOverlap(input.target, other),
  );

  const blockingKeeper = overlapping.find((other) =>
    squadGuestsInto(input.target.squadName, other.squadName),
  );
  if (
    blockingKeeper &&
    !isKeptPerformanceMorningOnDate({
      squadName: input.target.squadName,
      sessionDate: input.target.sessionDate,
      startTime: input.target.startTime,
    })
  ) {
    const name = blockingKeeper.squadName?.trim() || 'shared';
    return {
      action: 'reject',
      message: `This time is the ${name} session. Book that one.`,
    };
  }

  const absorb = overlapping.filter(
    (other) =>
      occupying.has(other.id) &&
      squadGuestsInto(other.squadName, input.target.squadName) &&
      !isKeptPerformanceMorningOnDate({
        squadName: other.squadName,
        sessionDate: other.sessionDate,
        startTime: other.startTime,
      }),
  );
  const blocking = overlapping.filter(
    (other) => occupying.has(other.id) && !absorb.some((row) => row.id === other.id),
  );
  if (blocking.length > 0) {
    return {
      action: 'reject',
      message: 'Already booked on another session at this time in the same pool.',
    };
  }
  if (absorb.length > 0) return { action: 'absorb', duplicateIds: absorb.map((row) => row.id) };
  return { action: 'allow' };
}

const ROLL_RANK: Record<string, number> = {
  checked_in: 4,
  booked: 3,
  waitlist: 2,
  cancelled: 1,
};

export function rollStatusRank(status: string | null | undefined): number {
  if (!status) return 0;
  return ROLL_RANK[status] ?? 0;
}

/** One row per swimmer. A stronger status on either session wins. */
export function mergeRollBySwimmer<T>(rows: readonly T[], swimmerId: (row: T) => string, status: (row: T) => string): T[] {
  const byId = new Map<string, T>();
  for (const row of rows) {
    const id = swimmerId(row);
    const have = byId.get(id);
    if (!have || rollStatusRank(status(row)) > rollStatusRank(status(have))) byId.set(id, row);
  }
  return [...byId.values()];
}

export type SharedTemplatePlan = {
  ok: true;
  retire: { templateId: string; keeperTemplateId: string }[];
  keptMorningIds: string[];
  /** Tue/Thu mornings that overlap Development. Left in place on purpose. */
  morningsLeftOverlapping: string[];
};

/**
 * Which Performance templates duplicate a Development template.
 * Refuses when Tuesday or Thursday morning Performance is missing, so a bad
 * read cannot retire the block parents must keep.
 */
export function planSharedPerformanceTemplates(
  templates: readonly SharedSlotTemplate[],
): SharedTemplatePlan | { ok: false; reason: string } {
  const performance = templates.filter((row) => normSquad(row.squadName) === 'performance');
  const mornings = performance.filter((row) =>
    isKeptPerformanceMorning({
      squadName: row.squadName,
      dayOfWeek: row.dayOfWeek,
      startTime: row.startTime,
    }),
  );
  const hasTuesday = mornings.some((row) => row.dayOfWeek === 2);
  const hasThursday = mornings.some((row) => row.dayOfWeek === 4);
  if (!hasTuesday || !hasThursday) {
    return {
      ok: false,
      reason: 'Tuesday and Thursday morning Performance sessions must stay on the timetable.',
    };
  }

  const development = templates.filter((row) => normSquad(row.squadName) === 'development');
  const retire: SharedTemplatePlan['retire'] = [];
  const morningsLeftOverlapping: string[] = [];
  const morningIds = new Set(mornings.map((row) => row.id));

  for (const row of performance) {
    const keeper = development.find(
      (other) =>
        other.dayOfWeek === row.dayOfWeek &&
        samePool(other.poolKey, row.poolKey) &&
        timesOverlap(row.startTime, row.endTime, other.startTime, other.endTime),
    );
    if (!keeper) continue;
    if (morningIds.has(row.id)) {
      morningsLeftOverlapping.push(row.id);
      continue;
    }
    retire.push({ templateId: row.id, keeperTemplateId: keeper.id });
  }

  return {
    ok: true,
    retire,
    keptMorningIds: mornings.map((row) => row.id),
    morningsLeftOverlapping,
  };
}
